import { clsx } from "clsx";
import { useCallback, useEffect } from "react";
import { Field } from "../../../../externalComponents/tailwind/fieldset";
import SelectInput from "../../../../externalComponents/tailwind/select-input";
import { Text } from "../../../../externalComponents/tailwind/text";
import { formatDateTime } from "../../../../functions/date-utils";

const SendCampaignPacing = ({
  className,
  expanded,
  form,
  setForm,
  messagingLimit,
}) => {
  const updatePacing = useCallback(
    (_pacing) => {
      setForm((_form) => ({
        ..._form,
        pacing: {
          ..._form.pacing,
          ..._pacing,
        },
      }));
    },
    [form.pacing],
  );

  const updatePaceOverDays = useCallback(
    (paceOverDays) => {
      updatePacing({ paceOverDays });
    },
    [form.pacing],
  );

  const calculateDefaultDays = useCallback(() => {
    const totalContacts = form.pacing.effectiveSelectedContacts.length;
    const minimumDays = Math.ceil(totalContacts / messagingLimit);
    return Math.min(Math.max(minimumDays, 1), 7);
  }, [form.pacing.effectiveSelectedContacts.length, messagingLimit]);

  const getDayLabel = useCallback(
    (index) => {
      const initialDateTime =
        form.isSendingNow === false ? form.scheduleDateTime : new Date();
      const date = new Date(initialDateTime);
      date.setDate(date.getDate() + index);
      const today = new Date();
      const tomorrow = new Date();
      tomorrow.setDate(today.getDate() + 1);

      if (date.toDateString() === today.toDateString()) {
        return "Today";
      }
      if (date.toDateString() === tomorrow.toDateString()) {
        return "Tomorrow";
      }
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
    },
    [form.scheduleDateTime, form.isSendingNow],
  );

  useEffect(() => {
    const _paceOverDays = calculateDefaultDays();
    const totalContacts = form.pacing.effectiveSelectedContacts.length;

    const baseCount = Math.floor(totalContacts / _paceOverDays);
    const remainder = totalContacts % _paceOverDays;

    const _planning = Array.from({ length: _paceOverDays }).map((_, index) => ({
      dayIndex: index,
      dayLabel: getDayLabel(index),
      contacts: index < remainder ? baseCount + 1 : baseCount,
      skipped: false,
    }));
    updatePacing({
      planning: _planning,
      paceOverDays: _paceOverDays,
    });
  }, [calculateDefaultDays]);

  useEffect(() => {
    const totalContacts = form.pacing.effectiveSelectedContacts.length;
    const minimumDays = Math.ceil(totalContacts / messagingLimit);
    let disclaimer = null;
    if (form.pacing.paceOverDays < minimumDays) {
      disclaimer = `Based on your current messaging limit you are advised to send this campaign in at least ${minimumDays} days`;
    }

    const realDays = form.pacing.paceOverDays;
    const baseCount = Math.floor(totalContacts / realDays);
    const remainder = totalContacts % realDays;

    const newPlan = Array.from({ length: realDays }).map((_, index) => ({
      dayIndex: index,
      dayLabel: getDayLabel(index),
      contacts: index < remainder ? baseCount + 1 : baseCount,
      skipped: false,
    }));
    const _pacing = {
      planning: newPlan,
      skippedDaysSet: new Set(),
      disclaimer,
    };
    updatePacing(_pacing);
  }, [
    form.pacing.paceOverDays,
    form.pacing.effectiveSelectedContacts.length,
    form.scheduleDateTime,
    form.isSendingNow,
  ]);

  const handleDayToggle = useCallback(
    (dayIndex) => {
      const alreadySkipped = form.pacing.skippedDaysSet.has(dayIndex);

      if (alreadySkipped) {
        // If the day is re-added, remove the last day.
        form.pacing.planning.pop();
      }

      const _planning = form.pacing.planning.map((day, index) => {
        let skipped = day.skipped;
        if (index === dayIndex) {
          skipped = !alreadySkipped;
        }
        return {
          ...day,
          skipped,
        };
      });
      let totalRecipientsInDayPlan = _planning.reduce(
        (sum, { contacts, skipped }) => (sum += !skipped && contacts),
        0,
      );
      if (!alreadySkipped) {
        const lastIndex = _planning.length;
        const totalContacts = form.pacing.effectiveSelectedContacts.length;

        const _contacts = _planning[dayIndex].contacts;
        if (totalRecipientsInDayPlan + _contacts <= totalContacts) {
          _planning.push({
            dayIndex: lastIndex,
            dayLabel: getDayLabel(lastIndex),
            contacts: _contacts,
            skipped: false,
          });
          totalRecipientsInDayPlan += _contacts;
        }
      }

      let _disclaimer = null;
      if (
        totalRecipientsInDayPlan -
          form.pacing.effectiveSelectedContacts.length >
        0
      ) {
        _disclaimer = `${totalRecipientsInDayPlan - form.pacing.effectiveSelectedContacts.length} recipients must to be unselected`;
      }

      const _skippedDaysSet = new Set(form.pacing.skippedDaysSet);
      if (form.pacing.skippedDaysSet.has(dayIndex)) {
        _skippedDaysSet.delete(dayIndex);
      } else {
        _skippedDaysSet.add(dayIndex);
      }
      const _pacing = {
        planning: _planning,
        skippedDaysSet: _skippedDaysSet,
        disclaimer: _disclaimer,
      };
      updatePacing(_pacing);
    },
    [form.pacing],
  );

  const daysOptions = Array.from({ length: 7 }).map((_, index) => index + 1);
  const initialDateTime =
    form.isSendingNow === false ? form.scheduleDateTime : new Date();
  return (
    <Field
      className={clsx("border rounded-customBorderRadius p-3 mt-5", className)}
    >
      <Text className="font-bold">4. Pacing</Text>
      {expanded && (
        <>
          {form.pacing.effectiveSelectedContacts.length ? (
            <>
              <div className="inline-flex items-center gap-2 mt-5">
                Split sendings over:&nbsp;
                <div className="w-[50px]">
                  <SelectInput
                    onChange={(value) => updatePaceOverDays(Number(value))}
                    options={daysOptions}
                    selected={form.pacing.paceOverDays}
                    key="pace-over-days"
                  />
                </div>
                day{form.pacing.paceOverDays > 1 ? "s" : ""}
                {form.pacing.paceOverDays === 1 && " (send all at once)"}
              </div>

              {form.pacing.disclaimer && (
                <div className="mt-2 text-red-600 text-sm">
                  {form.pacing.disclaimer}
                </div>
              )}

              <div className="mt-5">
                {form.pacing.planning
                  .sort((a, b) => a.dayIndex - b.dayIndex)
                  .map((day, index) => (
                    <div
                      key={day.dayIndex}
                      className={clsx({
                        "line-through": form.pacing.skippedDaysSet.has(
                          day.dayIndex,
                        ),
                      })}
                      data-testid={`plan-day-${index}`}
                    >
                      Send to {day.contacts} recipients&nbsp;
                      <strong>{day.dayLabel}</strong>
                      {form.isSendingNow === false && (
                        <>
                          {" "}
                          from {formatDateTime(initialDateTime, "shortTime")}
                        </>
                      )}
                      <button
                        className="text-blue-600 cursor-pointer ml-2"
                        onClick={() => handleDayToggle(day.dayIndex)}
                      >
                        {form.pacing.skippedDaysSet.has(day.dayIndex)
                          ? "Add that day"
                          : "Not that day"}
                      </button>
                    </div>
                  ))}
              </div>
            </>
          ) : (
            <div>No contacts selected</div>
          )}
        </>
      )}
    </Field>
  );
};

export default SendCampaignPacing;
